import { KPI } from '@/components/ui/KPI';
import { SectionHead } from '@/components/ui/SectionHead';
import { ChartCard } from '@/components/ui/ChartCard';
import { DailyBars } from '@/components/DailyBars';
import type { ConsentsSection, Count } from '@/api/worldCup';
import styles from './ConsentsView.module.css';

/*
 * France-only sub-tab. Currently shown only when the payload ships a
 * `sections.consents` block — DashboardPage gates the tab + the route.
 *
 * Two widgets:
 *   1. Per-type breakdown (e.g. marketing / partner / data — pipeline returns
 *      whatever consent types the loyalty system actually records).
 *   2. Daily series for the campaign window — granted-per-day. Empty days
 *      arrive as value: 0 from the backend so the chart is gap-free.
 */

const numFmt = new Intl.NumberFormat('en-US');

type Props = {
  section: ConsentsSection;
  /** Headline KPI from overview.business_kpis.consents_total. Optional — the
   *  section can stand alone if the headline is missing for some reason. */
  total?: Count;
};

// Humanize raw consent-type keys for display when the backend doesn't supply
// a friendly `label`. e.g. `data_sharing_partners` → `Data sharing partners`.
function humanize(type: string): string {
  const spaced = type.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function ConsentsView({ section, total }: Props) {
  const max = section.by_type.reduce((m, r) => Math.max(m, r.count), 0) || 1;

  return (
    <div>
      <SectionHead tag="consents">Consents given</SectionHead>
      <div className="grid-2">{total && <KPI label="Consents given" value={total.display} accent="engage" foot={total.foot} />}</div>

      <SectionHead tag="trends">Consents per day</SectionHead>
      <div className="grid-1">
        <ChartCard title={section.by_day.label} unit={section.by_day.unit}>
          <DailyBars series={section.by_day} color="var(--accent)" />
        </ChartCard>
      </div>

      {section.by_type.length > 0 && (
        <>
          <SectionHead tag="breakdown">By consent type</SectionHead>
          <ChartCard title="Distinct users per consent type">
            <ul className={styles.typeList}>
              {section.by_type.map((row) => (
                <li key={row.type} className={styles.typeRow}>
                  <span className={`mono ${styles.typeName}`}>{row.label ?? humanize(row.type)}</span>
                  <div className={styles.typeTrack} aria-hidden>
                    <div className={styles.typeFill} style={{ width: `${(row.count / max) * 100}%` }} />
                  </div>
                  <span className={`mono ${styles.typeValue}`}>{numFmt.format(row.count)}</span>
                </li>
              ))}
            </ul>
          </ChartCard>
        </>
      )}
    </div>
  );
}
